<script module lang="ts">
	// Drivers (T34, UC-65, #1003): a manager keeps this shop's drivers from
	// the till, in a Sheet over Till settings (T26), laid out as the
	// dashboard's tables (D23): the list in name order, a name changed in
	// place, a new driver as a blank row at the end, and Remove with the
	// 10-second Undo, which Till.svelte draws. The list and its changes are
	// src/till/tillDrivers.svelte.ts's; every rule is the server's.
	//
	// Offline, a store that keeps the list answers it from what it kept, and
	// the Sheet shows it read-only with a line that a change needs the
	// internet. A store that keeps none answers no list, and the Sheet says it
	// could not be loaded, with Retry.

	/** What the Sheet shows for its read: nothing for its first 300 ms, then the compass, then its answer. */
	export function driversReadState(drivers: {
		loading: boolean;
		slow: boolean;
		failed: boolean;
		forbidden: boolean;
		readOk: boolean;
	}): 'waiting' | 'loading' | 'failed' | 'forbidden' | 'ready' {
		if (drivers.forbidden && !drivers.readOk) return 'forbidden';
		if (drivers.readOk && !drivers.failed) return 'ready';
		if (drivers.loading) return drivers.slow ? 'loading' : 'waiting';
		return drivers.failed ? 'failed' : 'waiting';
	}
</script>

<script lang="ts">
	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import { message, type Language, type MessageKey } from '../i18n/messages';
	import { driverChangeWords, type TillDrivers } from '../till/tillDrivers.svelte';

	interface Props {
		language: Language;
		drivers: TillDrivers;
		/** The store does not reach Bosla: the list reads, and nothing changes. */
		offline: boolean;
	}

	let { language, drivers, offline }: Props = $props();

	let failure = $state<MessageKey | null>(null);
	const edit = $derived(drivers.edit);

	const readState = $derived(driversReadState(drivers));
	const ready = $derived(readState === 'ready');
	const editable = $derived(ready && !offline && !drivers.forbidden);
	const rows = $derived([
		...drivers.shown,
		...(editable && edit?.id === 'new' ? [{ id: 'new', name: '' }] : []),
	]);

	function startNew() {
		failure = null;
		drivers.startEdit(null);
	}

	async function save() {
		const current = drivers.edit;
		if (!current || !current.name.trim() || drivers.saving) return;
		failure = null;
		const change = await drivers.save(current.id === 'new' ? null : current.id, current.name);
		// Typing while it was on its way keeps the same row: only a Cancel, or
		// a removal by someone else, ends it.
		const now = drivers.edit;
		if (now?.id !== current.id) return;
		if (change === 'saved') {
			drivers.edit = null;
		} else if (change === 'taken' || change === 'bounds') {
			drivers.edit = { ...now, error: driverChangeWords(change) };
		} else if (change === 'gone') {
			// The reread ends the edit once a list answered without him; a
			// server without the drivers' routes answers none, and the name stays.
			failure = driverChangeWords(change);
		} else {
			// The name stays as typed, to save again once Bosla answers.
			failure = driverChangeWords(change);
		}
	}
</script>

<div class="drivers" data-testid="till-drivers">
	{#if readState === 'loading'}
		<div class="wait">
			<Loading
				label={message(language, 'tillDriversLoading')}
				caption={message(language, 'tillDriversLoading')}
			/>
		</div>
	{:else if readState === 'forbidden'}
		<Band tone="flag" alert testId="till-drivers-forbidden">
			{message(language, 'tillDriversForbidden')}
		</Band>
	{:else if readState === 'failed'}
		<Band
			tone="flag"
			alert
			testId="till-drivers-unread"
			action={drivers.retryable ? message(language, 'doneRetry') : undefined}
			actionTestId="till-drivers-retry"
			onAction={drivers.retryable ? () => void drivers.read() : undefined}
		>
			{message(language, 'tillDriversLoadFailed')}
		</Band>
	{/if}
	{#if ready}
		{#if offline}
			<Band tone="flag" testId="till-drivers-offline">
				{message(language, 'tillDriversNeedsInternet')}
			</Band>
		{/if}
		{#if rows.length === 0}
			<p class="empty" data-testid="till-drivers-empty">{message(language, 'tillDriversEmpty')}</p>
		{/if}
		<div class="list" role="list">
			{#each rows as driver (driver.id)}
				<div class="row" role="listitem" data-testid="till-driver-{driver.id}">
					{#if edit && editable && edit.id === driver.id}
						<div class="name-field">
							<Field
								testId="till-driver-name"
								ariaLabel={message(language, 'tillDriversName')}
								value={edit.name}
								maxLength={200}
								error={edit.error ? message(language, edit.error) : undefined}
								errorTestId="till-driver-name-error"
								onInput={(v) => {
									if (drivers.edit) drivers.edit = { ...drivers.edit, name: v, error: null };
								}}
							/>
						</div>
						<Button
							testId="till-driver-save"
							label={message(language, 'save')}
							size="sm"
							state={drivers.savingSlow ? 'pending' : 'idle'}
							pendingLabel={message(language, 'saving')}
							disabled={!edit.name.trim() || drivers.saving}
							onPress={() => void save()}
						/>
						<Button
							testId="till-driver-cancel"
							label={message(language, 'cancel')}
							tone="text-quiet"
							size="sm"
							disabled={drivers.saving}
							onPress={() => {
								drivers.edit = null;
								failure = null;
							}}
						/>
					{:else}
						<span class="label"><bdi>{driver.name}</bdi></span>
						{#if editable}
							<Button
								testId="till-driver-edit-{driver.id}"
								label={message(language, 'edit')}
								tone="text"
								size="sm"
								disabled={edit !== null}
								onPress={() => {
									failure = null;
									drivers.startEdit(driver);
								}}
							/>
							<Button
								testId="till-driver-remove-{driver.id}"
								label={message(language, 'remove')}
								tone="text-quiet"
								size="sm"
								disabled={edit !== null}
								onPress={() => {
									failure = null;
									drivers.remove(driver);
								}}
							/>
						{/if}
					{/if}
				</div>
			{/each}
		</div>
	{/if}
	{#if failure || drivers.failure}
		<p class="said" role="alert" data-testid="till-drivers-failure">
			{message(language, (failure ?? drivers.failure) as MessageKey)}
		</p>
	{/if}
	{#if editable}
		<div class="add">
			<Button
				testId="till-driver-add"
				icon="plus"
				label={message(language, 'tillDriversAdd')}
				tone="secondary"
				size="sm"
				disabled={edit !== null}
				onPress={startNew}
			/>
		</div>
	{/if}
</div>

<style>
	.drivers {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
		min-block-size: 0;
	}

	.wait {
		display: flex;
		justify-content: center;
		padding-block: var(--space-9);
	}

	.empty {
		margin: 0;
		padding: var(--card-pad);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.list {
		display: flex;
		flex-direction: column;
	}

	.row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-5);
		padding: var(--row-pad);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.label {
		flex: 1;
		min-inline-size: 0;
		font-size: var(--text-sm);
		font-weight: 600;
		overflow-wrap: anywhere;
	}

	.name-field {
		flex: 1 1 160px;
		min-inline-size: 0;
	}

	.add {
		display: flex;
		padding: var(--row-pad);
	}

	.said {
		margin: 0;
		padding-inline: var(--space-4);
		font-size: var(--text-2xs);
		font-weight: 600;
		color: var(--ink-flag-deep);
	}
</style>
